import { Box, Button, Chip, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import { priceFormatter } from '~/utils/priceFormatter';
import { PIX_SCAN_TIME_SECONDS } from '../../constants';
import { PixStyles } from './styles';

interface PixPaymentProps {
  qrCodeValue: string | null;
  pixValue: number;
  onGenerateQRCode: () => void;
  setQrCodeValue: (value: string | null) => void;
  timeLeft: number;
  paymentConfirmed?: boolean;
  /** Desabilita o botão de gerar QR (ex.: cotação ainda carregando) */
  disableGenerateButton?: boolean;
}

export function PixPayment({
  qrCodeValue,
  onGenerateQRCode,
  setQrCodeValue: _setQrCodeValue,
  pixValue,
  timeLeft,
  paymentConfirmed = false,
  disableGenerateButton = false
}: PixPaymentProps) {
  const theme = useTheme();
  const styles = PixStyles(theme);
  const [isQRCodeVisible, setIsQRCodeVisible] = useState(false);

  useEffect(() => {
    if (qrCodeValue !== null) {
      setIsQRCodeVisible(true);
    } else {
      setIsQRCodeVisible(false);
    }
  }, [qrCodeValue]);

  const formatTime = (seconds: number) => {
    if (isNaN(seconds) || seconds < 0) return '0:00'; // Garante que valores inválidos sejam tratados
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const copyPixCode = () => {
    navigator.clipboard.writeText(qrCodeValue || '');
  };

  return (
    <Box sx={styles.mainContainer}>
      <Typography variant="h6">
        Dados de contratação
        {paymentConfirmed && (
          <Box component="span" sx={{ color: 'success.main', ml: 2, display: 'inline-flex', alignItems: 'center' }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M8 12l2 2 4-4"></path>
            </svg>
            <Typography variant="caption" sx={{ ml: 0.5 }}>
              Pagamento confirmado
            </Typography>
          </Box>
        )}
      </Typography>
      <Box sx={styles.textContainer}>
        <Typography variant="body1" sx={styles.text}>
          {`Você tem ${Math.floor(PIX_SCAN_TIME_SECONDS / 60)} minutos para efetuar o pagamento:`}
        </Typography>
        <Typography variant="body1" sx={styles.text}>
          Valor:
          <Typography>R$ {priceFormatter(pixValue)}</Typography>
        </Typography>
      </Box>
      <Box sx={styles.secondaryContainer}>
        <Box sx={styles.actionButtonContainer}>
          {isQRCodeVisible && (
            <Chip
              label={paymentConfirmed ? 'Pagamento confirmado' : formatTime(timeLeft)}
              variant="filled"
              color={paymentConfirmed ? 'success' : timeLeft > 0 ? 'success' : 'error'}
              sx={styles.timeChip}
            />
          )}
          <Button
            variant="outlined"
            sx={styles.actionButton}
            onClick={onGenerateQRCode}
            disabled={!!qrCodeValue || paymentConfirmed || disableGenerateButton}
          >
            Gerar código QR
          </Button>
          <Button
            variant="outlined"
            sx={styles.actionButton}
            onClick={copyPixCode}
            disabled={!qrCodeValue || paymentConfirmed}
          >
            Copiar código Pix
          </Button>
        </Box>

        <Box sx={styles.pixContainer}>
          {isQRCodeVisible ? (
            <QRCode
              value={qrCodeValue || ''}
              size={144}
              bgColor={theme.customColors.purple[750]}
              fgColor={paymentConfirmed ? '#4CAF50' : theme.customColors.purple[400]}
            />
          ) : (
            <Typography variant="body2" sx={styles.text}>
              Clique no botão &quot;Gerar código QR&quot; para visualizar o código.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
